import Plugin from '../core/Plugin';
import TencentInject from './lag/TencentInject';
import { PlayerEvent } from '../PlayerEvents';
import log from 'loglevel';

/**
 *
 * Distributed under the MIT license. See LICENSE file for details.
 * Copyright (c) 2019 vhall
 */

/**
 * 追播插件
 *
 * @export
 * @class Pursue
 */
export default class Pursue extends Plugin {
  constructor() {
    super();

    this.bufferMax = -1;
    this.remain = 2;
    this.speed = 1.2;
    this.mode = 'pursue';

    this._isPursuing = false;
  }

  init(opts = {}) {
    if (opts['isLive'] && opts['isOpenTimeshift'] && opts['type'] === 'hls') {
      log.info('时移直播不进行追播');
      return;
    }

    super.init(opts);
    this._allConfig = opts;
    this.pursueConfig = JSON.parse(opts[Pursue.type]);

    if (!this.pursueConfig['enable']) {
      log.info('未启用追播功能');
      return;
    }

    this._isLive = opts['isLive'];

    // 点播活动不进行追播
    if (!this._isLive) {
      log.debug('点播活动不进行追播策略');
      return;
    }

    if (this.player.player._type === 'FlashPlayer') {
      log.debug('flash不进行追播');
      return;
    }

    // 腾讯安卓系列, 追播模式为倍速播放。不进行追播
    if (TencentInject.isSupported()) {
      log.info('腾讯安卓系列不进行追播,x5内核不支持');
      return;
    }

    const type = this._allConfig['type'];
    const cfg = this.pursueConfig[type] || { bufferMax: 10, remain: 2, pursueSpeed: 1.2 };
    this.bufferMax = cfg['bufferMax'];
    this.remain = cfg['remain'];
    this.speed = cfg['pursueSpeed'];
    this.mode = this.pursueConfig['mode'];

    log.info(`播放类型:${type},Buffer最大缓冲时长:${this.bufferMax}s, 以${this.speed}倍速追到剩${this.remain}s结束.`);
    this._handleEvents();
  }

  _handleEvents() {
    this.player.on(PlayerEvent.TIMEUPDATE, this.__onTimeupdate.bind(this));
  }

  __onTimeupdate(e) {
    // 判断是否需要进行追播
    // 1: 判断buffer最大值， 2：判断当前倍速，3:判断当前时间与buffer差值时间
    const player = this.player;
    const buffered = player.getBuffered();
    const bufferedLen = buffered.length;
    let buffer = bufferedLen >= 1 ? buffered.end(bufferedLen - 1) : 0;
    // 计算时差
    let elapsed = buffer - player.getCurrentTime();
    // 如果超过最大值 或者 追播过程中但是还没追到
    if (elapsed > this.bufferMax || (this._isPursuing && elapsed > this.remain)) {
      // 根据追播模式，选择追播还是跳播
      // log.info(`自动追播 -> 触发条件，elapsed为${elapsed}，bufferMax为${this.bufferMax}`);
      const mode = this.mode;
      switch (mode) {
        case 'pursue':
          // 如果差值大于三倍的bufferMax时间，就跳过去，如，设置bufferMax为10， 如果差值超过30秒，就直接跳到最新，不追了
          if (elapsed > 6 * this.bufferMax) {
            // log.info(`自动追播 -> 加速模式，seek至${buffer - this.remain}`);
            this.jumpTo(buffer - this.remain);
          } else {
            // log.info(`自动追播 -> 加速模式，播放速度调整为${this.speed}`);
            this.run();
          }
          break;
        case 'seek':
          // log.info(`自动追播 -> 跳转模式，seek至${buffer - this.remain}`);
          this.jumpTo(buffer - this.remain);
          break;
        default:
          log.warn(`位置追播类型${mode}`);
          break;
      }
    } else {
      this.walk();
    }

    // log.debug(`当前${player.getCurrentTime()},剩余${buffer},差值${elapsed},是否正在追${this.isPursuing}`);
  }

  /**
   * 开始追博
   *
   * @memberof Pursue
   */
  run() {
    this._isPursuing = true;
    this.player.setPlaybackRate(this.speed);
  }

  /**
   * 停止追播，正常播放
   *
   * @memberof Pursue
   */
  walk() {
    this._isPursuing = false;
    this.player.setPlaybackRate(1);
  }

  /**
   * seek 类型追播
   *
   * @memberof Pursue
   */
  jumpTo(t) {
    if (t <= 0) {
      t = 0;
    }

    this.player.setCurrentTime(t);
    this.player.setPlaybackRate(1);
  }

  destroy() {
    super.destroy();
    this.bufferMax = -1;
    this.remain = 2;
    this.speed = 1.2;
    this._isPursuing = false;

    // 清理事件监听
    this.player.off(PlayerEvent.TIMEUPDATE, this.__onTimeupdate);
    this.player = null;
  }

  /**
   * 是否正在处于追播状态
   *
   * @readonly
   * @memberof Pursue
   */
  get isPursuing() {
    return this._isPursuing;
  }

  static get type() {
    return 'plugin_pursue';
  }
}
